/* Pipeline 172X: trial usage components
   API: GET /usage, GET /product-catalog
   All balances, entitlements, and allowed actions come from the server.
   The frontend never calculates authoritative usage.

   Reusable across: Today dashboard, campaign execution preview,
   usage/subscription settings, pre-action confirmation dialogs. */

import type { TrialBalanceState, UsageBalance } from "../../types/auth";

/* ─── Color helpers ────────────────────────────────────────────── */
function balanceColor(state: TrialBalanceState): string {
  if (state === "exhausted" || state === "canceled") return "#dc2626";
  if (state === "critical") return "#b45309";
  if (state === "low") return "#d97706";
  if (state === "full" || state === "upgraded") return "#15803d";
  return "#1a3a5c";
}

function balanceBg(state: TrialBalanceState): string {
  if (state === "exhausted" || state === "canceled") return "#fef2f2";
  if (state === "critical") return "#fffbeb";
  if (state === "low") return "#fffbeb";
  if (state === "full" || state === "upgraded") return "#f0fdf4";
  return "#eff6ff";
}

/* ─── TrialBadge ───────────────────────────────────────────────── */
/** Compact inline badge: suitable for nav bars and page headers. */
export function TrialBadge({ balance }: { balance: UsageBalance }) {
  if (balance.state === "upgraded") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontFamily: "DM Mono, monospace",
          fontSize: 11,
          fontWeight: 500,
          color: "#15803d",
          background: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: 6,
          padding: "3px 8px",
          letterSpacing: "0.02em",
        }}
      >
        <span aria-hidden="true">●</span> Paid plan active
      </span>
    );
  }

  if (balance.state === "catalog-error" || balance.state === "stale") {
    return (
      <span
        title="Usage data temporarily unavailable. The server will enforce limits."
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontFamily: "DM Mono, monospace",
          fontSize: 11,
          color: "#6b7280",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          padding: "3px 8px",
        }}
      >
        Usage unavailable
      </span>
    );
  }

  const color = balanceColor(balance.state);
  const bg = balanceBg(balance.state);
  const label =
    balance.remaining !== null
      ? `${balance.remaining} of ${balance.total ?? "?"} searches`
      : "Balance unavailable";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontFamily: "DM Mono, monospace",
        fontSize: 11,
        fontWeight: 500,
        color,
        background: bg,
        border: `1px solid ${color}33`,
        borderRadius: 6,
        padding: "3px 8px",
        letterSpacing: "0.01em",
      }}
      title="Trial job search allowance. Remaining balance from server."
      aria-label={`Trial: ${label}`}
    >
      {balance.isTrial && (
        <span aria-hidden="true" style={{ fontSize: 9 }}>
          TRIAL
        </span>
      )}
      {balance.remaining !== null ? balance.remaining : "N/A"}
    </span>
  );
}

/* ─── TrialUsageMeter ──────────────────────────────────────────── */
/** Full usage meter: suitable for dashboard widgets and settings. */
export function TrialUsageMeter({
  balance,
  onUpgrade,
}: {
  balance: UsageBalance;
  onUpgrade?: () => void;
}) {
  const pct =
    balance.remaining !== null && balance.total
      ? Math.round(((balance.total - balance.remaining) / balance.total) * 100)
      : 0;

  const color = balanceColor(balance.state);

  if (balance.state === "catalog-error") {
    return <EntitlementError />;
  }

  if (balance.state === "upgraded") {
    return (
      <div
        style={{
          padding: "16px 18px",
          background: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: 10,
        }}
        role="status"
        aria-label="Paid plan active"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: 13,
              color: "#15803d",
            }}
          >
            Paid plan active
          </span>
          <span
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 11,
              color: "#15803d",
            }}
          >
            Unlimited
          </span>
        </div>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: "#166534",
            margin: 0,
          }}
        >
          Your plan allowance is managed by the server. Trial limits no longer
          apply.
        </p>
      </div>
    );
  }

  if (balance.state === "canceled") {
    return (
      <div
        style={{
          padding: "16px 18px",
          background: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: 10,
        }}
        role="status"
        aria-label="Subscription canceled. Saved data retained."
      >
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: 13,
            color: "#dc2626",
            marginBottom: 6,
          }}
        >
          Subscription canceled
        </div>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: "#991b1b",
            margin: "0 0 10px",
          }}
        >
          Your saved profile, evidence, matches, packages, and history remain
          accessible. Discovery and new applications are paused.
        </p>
        {onUpgrade && (
          <button
            onClick={onUpgrade}
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: 12,
              color: "#1a3a5c",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 7,
              padding: "6px 14px",
              cursor: "pointer",
            }}
          >
            View upgrade options
          </button>
        )}
      </div>
    );
  }

  if (balance.state === "stale") {
    return (
      <div
        style={{
          padding: "14px 16px",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
        }}
        role="status"
        aria-label="Usage data temporarily unavailable"
      >
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          Usage data temporarily unavailable. The server enforces limits
          regardless of display state.
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "16px 18px",
        background: balanceBg(balance.state),
        border: `1px solid ${color}33`,
        borderRadius: 10,
      }}
      role="status"
      aria-label={`Trial usage: ${balance.remaining ?? "?"} of ${balance.total ?? "?"} job evaluations remaining`}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: 13,
            color: "#111827",
          }}
        >
          {balance.isTrial ? "Free trial" : "Usage"}
        </span>
        <span
          style={{
            fontFamily: "DM Mono, monospace",
            fontSize: 13,
            fontWeight: 500,
            color,
          }}
        >
          {balance.remaining !== null ? balance.remaining : "N/A"} /{" "}
          {balance.total ?? "?"}
        </span>
      </div>

      {/* Bar */}
      <div
        style={{
          height: 6,
          background: "#e5e7eb",
          borderRadius: 99,
          overflow: "hidden",
          marginBottom: 8,
        }}
        aria-hidden="true"
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: color,
            borderRadius: 99,
            transition: "width 0.3s ease",
          }}
        />
      </div>

      {/* Label */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 11.5,
            color: "#6b7280",
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {balance.state === "full" &&
            "Your trial is active. Each discovery run consumes one search per the server-defined rule."}
          {balance.state === "active" &&
            `${balance.remaining} job evaluations remaining. Usage is enforced server-side.`}
          {balance.state === "low" &&
            `Only ${balance.remaining} searches remaining. Your saved work will not be deleted when the allowance is exhausted.`}
          {balance.state === "critical" &&
            "One search remaining. Your saved profile, evidence, and packages will remain accessible after this is used."}
          {balance.state === "exhausted" &&
            "Allowance exhausted. Saved work, evidence, matches, and history remain accessible. Discovery is paused."}
        </p>
        {(balance.state === "low" ||
          balance.state === "critical" ||
          balance.state === "exhausted") &&
          onUpgrade && (
            <button
              onClick={onUpgrade}
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: 11.5,
                color: "#1a3a5c",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 6,
                padding: "5px 12px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Upgrade
            </button>
          )}
      </div>

      {/* Exhausted extended message */}
      {balance.state === "exhausted" && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            background: "rgba(255,255,255,0.7)",
            borderRadius: 8,
            border: "1px solid #fecaca",
          }}
        >
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 11.5,
              color: "#991b1b",
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            Paid plan details are loaded from the server catalog. No credit card
            was required during your trial. Upgrade options will be presented
            when available.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Pre-action usage confirmation ──────────────────────────────── */
/** Shown before any action that consumes a search. */
export function PreActionUsageNote({
  balance,
  expectedCost,
}: {
  balance: UsageBalance;
  expectedCost: number | null;
}) {
  if (balance.state === "upgraded") return null;
  if (balance.remaining === null) return null;

  const willExhaust =
    expectedCost !== null && balance.remaining <= expectedCost;

  return (
    <div
      style={{
        padding: "10px 13px",
        background: willExhaust ? "#fffbeb" : "#eff6ff",
        border: `1px solid ${willExhaust ? "#fde68a" : "#bfdbfe"}`,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
      role="note"
    >
      <span
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 18,
          color: willExhaust ? "#b45309" : "#1a3a5c",
          fontWeight: 500,
        }}
      >
        {balance.remaining}
      </span>
      <div>
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12.5,
            fontWeight: 600,
            color: "#111827",
          }}
        >
          {expectedCost !== null
            ? `This action uses ${expectedCost} search.`
            : "This action uses 1 search."}{" "}
          {balance.remaining} remaining.
        </div>
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 11.5,
            color: "#6b7280",
            marginTop: 2,
          }}
        >
          Exact consumption is defined by the server. Saved work is retained if
          allowance is exhausted.
        </div>
      </div>
    </div>
  );
}

/* ─── Entitlement error ──────────────────────────────────────────── */
/** Shown when GET /product-catalog or GET /usage returns an error. */
export function EntitlementError() {
  return (
    <div
      style={{
        padding: "14px 16px",
        background: "#fff7ed",
        border: "1px solid #fed7aa",
        borderRadius: 10,
      }}
      role="alert"
      aria-label="Entitlement data unavailable"
    >
      <div
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 600,
          fontSize: 13,
          color: "#9a3412",
          marginBottom: 4,
        }}
      >
        Usage data temporarily unavailable
      </div>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          color: "#7c2d12",
          margin: 0,
          lineHeight: 1.5,
        }}
      >
        The server could not load your entitlement details. Actions that consume
        a search may be restricted until this is resolved. Your saved work is
        safe. Try refreshing.{" "}
        <span
          style={{
            fontFamily: "DM Mono, monospace",
            fontSize: 10.5,
            color: "#9a3412",
          }}
        >
          {/* API: GET /product-catalog, GET /usage */}
          API: GET /product-catalog · GET /usage
        </span>
      </p>
    </div>
  );
}

/* ─── ExhaustedGate ──────────────────────────────────────────────── */
/** Full-page gate for when a user hits an exhausted-trial restricted action. */
export function ExhaustedGate({ onUpgrade }: { onUpgrade?: () => void }) {
  return (
    <div
      style={{
        maxWidth: 520,
        margin: "0 auto",
        padding: "40px 24px",
        textAlign: "center",
      }}
      role="main"
      aria-label="Trial allowance exhausted"
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: "#fef2f2",
          border: "1px solid #fecaca",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px",
        }}
        aria-hidden="true"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="#dc2626" strokeWidth="1.75" />
          <path
            d="M12 7v5M12 14.5v.5"
            stroke="#dc2626"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <h2
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          fontSize: 20,
          color: "#111827",
          marginBottom: 10,
          letterSpacing: "-0.02em",
        }}
      >
        Your 150 job evaluations are used
      </h2>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 14,
          color: "#4b5563",
          lineHeight: 1.65,
          marginBottom: 24,
        }}
      >
        Your saved profile, evidence, job matches, packages, and history remain
        fully accessible. Discovery and new applications are paused until you
        upgrade.
      </p>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 12.5,
          color: "#6b7280",
          marginBottom: 28,
          lineHeight: 1.5,
        }}
      >
        Paid plan details are loaded from the server catalog. No pricing is
        hard-coded here. Your data is not deleted. There is no time pressure to
        upgrade.
      </p>

      {onUpgrade && (
        <button
          onClick={onUpgrade}
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: 14,
            color: "#ffffff",
            background: "#1a3a5c",
            border: "none",
            borderRadius: 9,
            padding: "12px 28px",
            cursor: "pointer",
            marginBottom: 12,
          }}
        >
          View upgrade options
        </button>
      )}
      <div>
        <a
          href="#"
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12.5,
            color: "#1a3a5c",
            textDecoration: "underline",
          }}
        >
          Browse your saved work
        </a>
      </div>
    </div>
  );
}
